/**
 * The reading screen (spec §2.2) — a progress screen, not a decision, so it
 * is uncounted and rendered outside `StepFrame`.
 *
 * Four tiles fill as the study lands each step. When it settles there are
 * THREE ways on, never a dead end: keep everything it found, walk through it
 * one screen at a time, or drop it and use ours. A read that landed nothing
 * says so in a sentence and hands over to the manual walk.
 */
import { Check, Loader2, MinusCircle, Sparkles, X } from "lucide-react";
import type { BrandStudyRecord, StudyStepId } from "../../../api";
import { STEP_COPY } from "../../../lib/brandSetup/steps";
import { StepHeader } from "../StepFrame";

const TILES: ReadonlyArray<{ id: StudyStepId; title: string; copy: string }> = [
  { id: "fetched", title: "Fetched", copy: "your links and files" },
  { id: "frames", title: "Frames", copy: "sampled from the video" },
  { id: "measured", title: "Measured", copy: "colors, sizes, position" },
  { id: "understood", title: "Understood", copy: "type, emphasis, pacing" },
];

function StatusMark({ status }: { status: string }): React.JSX.Element {
  if (status === "done") return <Check size={15} strokeWidth={3} style={{ color: "var(--ql-success)" }} />;
  if (status === "running") return <Loader2 size={15} className="ql-spin" style={{ color: "var(--ql-yellow)" }} />;
  if (status === "failed") return <X size={15} strokeWidth={3} style={{ color: "var(--ql-danger)" }} />;
  if (status === "skipped") return <MinusCircle size={15} style={{ color: "var(--ql-fg-faint)" }} />;
  return <span style={{ width: 15, height: 15, borderRadius: 999, border: "1.5px solid rgba(255,255,255,.14)" }} />;
}

export function ReadingStep(props: {
  record: BrandStudyRecord | null;
  /** Set when the study could not be started at all (409, network, timeout). */
  error: string | null;
  /** True once nothing is in flight any more — the ways on need that. */
  settled: boolean;
  /** How many of the findings have a step of their own. */
  matched: number;
  onKeepAll: () => void;
  onWalk: () => void;
  onDefaults: () => void;
  /** Nothing landed: carry on into the manual walk. */
  onContinue: () => void;
  onRetry: () => void;
  onEditSources: () => void;
}): React.JSX.Element {
  const { record, error, settled, matched } = props;
  const steps = record?.steps ?? [];
  const failures = record?.failures ?? [];
  const landed = (record?.chips ?? []).length > 0;
  const nothing = settled && !landed;
  // Vision fails OPEN — colours, sizes and position still land. It is a
  // PARTIAL read and it has to say so rather than pass for a clean one.
  const partial = settled && landed && !!record?.study?.visionError;

  return (
    <div style={{ display: "grid", gap: 24 }}>
      <StepHeader
        chapter="Basics"
        n={null}
        total={null}
        headline={settled ? (landed ? "Here's what we found." : "We couldn't read that.") : STEP_COPY.reading.headline("")}
        why={STEP_COPY.reading.why}
      />

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 10 }}>
        {TILES.map((t) => {
          const step = steps.find((s) => s.id === t.id);
          const status = step?.status ?? "pending";
          return (
            <div
              key={t.id}
              style={{
                display: "grid",
                gap: 6,
                padding: "16px 16px 18px",
                borderRadius: 16,
                border: status === "running" ? "1px solid rgba(255,213,0,.35)" : "1px solid rgba(255,255,255,.07)",
                background: status === "done" ? "rgba(255,255,255,.03)" : "rgba(255,255,255,.015)",
              }}
            >
              <StatusMark status={status} />
              <div style={{ fontSize: 15, fontWeight: 700, color: "var(--ql-fg)" }}>{t.title}</div>
              <div style={{ fontSize: 12.5, color: "var(--ql-fg-muted)", lineHeight: 1.5 }}>{step?.detail || t.copy}</div>
            </div>
          );
        })}
      </div>

      {partial && (
        <div style={{ fontSize: 13.5, color: "var(--ql-fg)", lineHeight: 1.6 }}>
          We measured your colors and layout, but couldn't read the type and the emphasis. Keep what we found, or set
          the rest yourself as we go.
        </div>
      )}

      {(failures.length > 0 || error) && (
        <div style={{ display: "grid", gap: 8 }}>
          {error && (
            <div style={{ fontSize: 13.5, color: "var(--ql-danger)", lineHeight: 1.6, whiteSpace: "pre-line" }}>{error}</div>
          )}
          {failures.map((f, i) => (
            <div key={i} style={{ fontSize: 13.5, color: "var(--ql-fg-muted)", lineHeight: 1.6 }}>
              {f.message}
            </div>
          ))}
        </div>
      )}

      {settled && landed && (
        <div style={{ display: "grid", gap: 14 }}>
          <div style={{ fontSize: 15, color: "var(--ql-fg)", lineHeight: 1.6 }}>
            We matched {matched} things. We'll show you each one as we go.
          </div>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
            <button type="button" autoFocus onClick={props.onKeepAll} style={primaryBtn}>
              <Sparkles size={15} />
              Keep all and continue
            </button>
            <button type="button" onClick={props.onWalk} style={secondaryBtn}>
              Walk me through each
            </button>
            <button type="button" onClick={props.onDefaults} style={linkBtn}>
              Use our defaults instead
            </button>
          </div>
        </div>
      )}

      {nothing && (
        <div style={{ display: "grid", gap: 14 }}>
          <div style={{ fontSize: 15, color: "var(--ql-fg)", lineHeight: 1.6 }}>
            We couldn't read anything from that. Let's set it up together.
          </div>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
            <button type="button" autoFocus onClick={props.onContinue} style={primaryBtn}>
              Continue
            </button>
            <button type="button" onClick={props.onEditSources} style={secondaryBtn}>
              Edit my sources
            </button>
            <button type="button" onClick={props.onRetry} style={linkBtn}>
              Try again
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const primaryBtn: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 9,
  padding: "14px 26px",
  borderRadius: 15,
  border: "none",
  background: "linear-gradient(135deg,#FFD500,#FFA500)",
  color: "#000",
  fontWeight: 800,
  fontSize: 14.5,
  fontFamily: "inherit",
  cursor: "pointer",
  boxShadow: "0 4px 16px rgba(255,213,0,.30)",
};

const secondaryBtn: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 7,
  padding: "13px 18px",
  borderRadius: 14,
  border: "1px solid rgba(255,255,255,.12)",
  background: "transparent",
  color: "var(--ql-fg)",
  fontSize: 13.5,
  fontWeight: 700,
  fontFamily: "inherit",
  cursor: "pointer",
};

const linkBtn: React.CSSProperties = {
  border: "none",
  background: "transparent",
  color: "var(--ql-fg-muted)",
  fontSize: 13,
  fontFamily: "inherit",
  textDecoration: "underline",
  cursor: "pointer",
  padding: 4,
};
